"use client";

import React, { useEffect, useMemo, useState } from "react";
import CartSvg from "../svg/header/cart";
import { Trans } from "@lingui/macro";
import KebabMenuSvg from "../svg/header/kebab-menu";
import SearchSvg from "../svg/header/search";
import HeaderMenu from "./header-menu";
import HeaderUserMenu from "./header/header-user-menu";
import Logo from "./logo-offerz";
import SearchBar from "./header/search-bar";
import Link from "next/link";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { getRequest, postRequest } from "@/utils/request";
import { useSession, signOut } from "next-auth/react";
function useSessionExpiry(callback: any) {
  const { data: session } = useSession();

  useEffect(() => {
    if (!session?.expires) return;

    const checkExpiry = () => {
      const expiresAt = new Date(session.expires).getTime();
      const now = Date.now();
      if (now >= expiresAt) {
        callback();
      }
    };
    const interval = setInterval(() => {
      checkExpiry();
      clearInterval(interval);
    }, 60_000);
    // Check immediately and on visibility change
    checkExpiry();
    window.addEventListener("visibilitychange", checkExpiry);

    return () => {
      window.removeEventListener("visibilitychange", checkExpiry);
    };
  }, [session?.expires, callback]);
}

function HeaderComponent({
  lang,
  dataMenu,
}: Readonly<{ lang: string; dataMenu: any }>): React.JSX.Element {
  const [searchModal, setSearchModal] = useState<boolean>(false);
  const [mobileMenu, setMobileMenu] = useState<boolean>(false);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const router = useRouter();
  const pathname = usePathname();
  const isCheckoutOrder = pathname?.includes("checkout-order");
  useSessionExpiry(async () => {
    await postRequest({ path: { name: "logout" }, method: "POST", lang });
    signOut({ callbackUrl: "/login" });
  });
  const { data, isLoading } = useQuery({
    queryKey: ["cartQuantity"],
    queryFn: () => getRequest({ path: { name: "cartQuantity" }, lang, skipCache: true }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });
  const { cartItems } = useMemo(() => {
    if (data?.success) {
      return {
        cartItems: data.data.quantity,
      };
    }
    return {
      cartItems: 0,
    };
  }, [data]);

  const handleOpenSearch = () => {
    setSearchModal(true);
  };
  const queryClient = useQueryClient();
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsSticky(scrollTop > 100);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleCartNavigation = () => {
    if (isCheckoutOrder) {
      queryClient.setQueryData(["cart"], (oldData: any) => {
        if (oldData) {
          return {
            ...oldData,
            data: {
              ...oldData.data,
              products: [],
              has_shipping: 0,
              has_download: 0,
              vouchers: [],
              coupon_status: 1,
              coupon: null,
              voucher_status: 1,
              voucher: null,
              totals: [
                {
                  title: "Sub-Total",
                  code: "sub_total",
                  text: "CHF 0.00",
                  value: 0,
                },
                {
                  title: "Total",
                  code: "total",
                  text: "CHF 0.00",
                  value: 0,
                },
              ],
              text_items: 0,
              total: 0,
              total_raw: 0,
              total_product_count: 0,
              total_loyalty_points: 0,
            },
          };
        }
        return oldData;
      });

      router.push("/cart");
    } else if (cartItems == 0) {
      router.push("/cart");
    } else {
      router.push("/checkout");
    }
  };
  useEffect(() => {
    const handleLoad = () => {
      setIsPageLoading(false);
    };

    if (document.readyState === "complete") {
      handleLoad(); // If page is already loaded
    } else {
      window.addEventListener("load", handleLoad);
    }

    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  const badgeClasses = `absolute text-xs right-0 top-0 flex items-center justify-center rounded-full bg-[#0097ab] text-center text-white ${
    cartItems > 999
      ? "h-[18px] w-[34px] !right-[-12px]"
      : cartItems > 99
        ? "h-[18px] w-[28px] !right-[-5px]"
        : "h-[18px] w-[18px]"
  }`;

  return (
    <>
      <header className="z-[49] border-b border-[#eee]">
        <div
          className={`container bg-white xs:fixed xs:border-b xs:border-[#eee] xs:border-b-[#eee] md:relative md:border-none ${
            isSticky ? "shadow-[0_0_10px_0_rgba(0,0,0,0.2)]" : "shadow-none"
          }`}
        >
          <div className="relative z-[2] flex w-full items-center bg-white py-2.5 md:justify-between md:py-[15px]">
            <div
              className="mr-5 block w-fit cursor-pointer md:hidden"
              onClick={() => setMobileMenu(true)}
            >
              <KebabMenuSvg />
            </div>
            <Logo />
            <div className="ml-auto flex items-center gap-[12px]">
              <Link href="/create-gift" prefetch={false} className="hidden pr-2.5 md:block">
                <span className="cursor-pointer whitespace-nowrap text-primary">
                  <Trans>Gift Designer</Trans>
                </span>
              </Link>
              <SearchBar lang={lang} searchModal={searchModal} setSearchModal={setSearchModal} />
              <div className="relative flex items-center gap-8">
                <div className="flex gap-1 md:gap-2">
                  <div
                    onClick={() => handleOpenSearch()}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center md:hidden md:h-[42px] md:w-[42px]"
                  >
                    <SearchSvg />
                  </div>
                  <HeaderUserMenu />
                  <div
                    onClick={isPageLoading || isLoading ? undefined : handleCartNavigation}
                    className="relative inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg md:h-[42px] md:w-[42px] md:hover:bg-[#eeeef0]"
                  >
                    <CartSvg />
                    {isPageLoading || isLoading ? (
                      <span className="absolute right-0 top-0 h-[18px] w-[18px] animate-pulse rounded-full bg-gray-300"></span>
                    ) : (
                      <div className={badgeClasses}>{isCheckoutOrder ? 0 : cartItems}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div
        className={`border-b border-[#eee] ${
          isSticky ? "fixed top-0 z-[101] w-full bg-white" : ""
        }`}
      >
        <div className="container">
          <HeaderMenu mobileMenu={mobileMenu} setMobileMenu={setMobileMenu} data={dataMenu} />
        </div>
      </div>
    </>
  );
}

export default React.memo(HeaderComponent);
